.card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  padding: var(--sp-4) var(--sp-4) var(--sp-4) var(--sp-5);
  background: var(--surface);
  border: var(--bw) solid var(--border);
  border-left: 3px solid var(--accent-color, var(--border-strong));
  border-radius: var(--radius);
  cursor: pointer;
  box-shadow: var(--glass-rim);
  /* Клик по новому блоку снимает подсветку (isNew ниже) — гасим её плавно,
     чтобы карточка не мигала под курсором в момент открытия формы. */
  transition: background-color 240ms var(--dnd-ease), border-color 240ms var(--dnd-ease);
}

.card:hover {
  border-color: var(--border-strong);
  box-shadow: var(--glass-rim), var(--shadow-card);
}

.card.dragging {
  opacity: 0.5;
}

/* Блок добавлен недавно и ещё не отмечен просмотренным (см. ui/library/newFlags.ts).
   Подсветка — акцент типа сегмента, разведённый в фон/обводку через color-mix:
   --warn/--error заняты под валидацию, свой цвет заводить незачем. */
.card.isNew {
  border-color: color-mix(in srgb, var(--accent-color, var(--focus)) 45%, var(--border));
  background: color-mix(in srgb, var(--accent-color, var(--focus)) 6%, var(--surface));
  animation: card-appear 420ms var(--dnd-ease) both;
}

@keyframes card-appear {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .card {
    transition: none;
  }

  .card.isNew {
    animation: none;
  }
}

.newBadge {
  flex: 0 0 auto;
  padding: 0 var(--sp-2);
  border-radius: var(--radius);
  background: color-mix(in srgb, var(--accent-color, var(--focus)) 22%, var(--surface));
  color: var(--accent-color, var(--focus));
  font-size: var(--fs-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.6;
}

.row1 {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-3);
}

.titleWrap {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-width: 0;
  color: var(--accent-color, var(--text));
}

.title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  font-size: var(--fs-md);
  color: var(--text);
}

.icon {
  flex: 0 0 auto;
  color: var(--accent-color, var(--text-3));
}

.price {
  flex: 0 0 auto;
  font-size: var(--fs-md);
  font-weight: 600;
}

.subtitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-3);
  font-size: var(--fs-sm);
}

/* Строка бейджа цены — только у рейсов. Карточка библиотеки узкая, поэтому
   min-width: 0 обязателен: без него название агрегатора не ужимается и рвёт
   ширину колонки. Пока проверка цен недоступна, PriceBadge отдаёт null —
   пустую строку гасим, иначе от неё остаётся отступ. */
.priceLine {
  display: flex;
  min-width: 0;
}

.priceLine:empty {
  display: none;
}

.priceLine > * {
  min-width: 0;
}

.metaRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  min-height: 20px;
}

.badge {
  flex: 0 0 auto;
  padding: 1px var(--sp-2);
  background: var(--surface-2);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius);
  color: var(--text-3);
  font-size: var(--fs-xs);
  white-space: nowrap;
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  opacity: 0;
  transition: opacity 80ms linear;
}

.card:hover .actions,
.card:focus-within .actions {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .actions {
    transition: none;
  }
}

.actionBtn {
  padding: var(--sp-1) var(--sp-2);
  font-size: var(--fs-xs);
}

/* Кнопка-иконка ряда: квадратик без подписи, имя живёт в aria-label. Размер —
   тот же, что у compact-кнопки проверки цены (модуль src/prices), иначе ряд
   рассыпается по высоте. */
.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
}

/* ── Мобильный вид (порог продублирован в ui/shell/useIsMobile.ts) ──────── */
@media (max-width: 720px) {
  /* В ленте библиотеки карточки тянутся по самой высокой в ряду. */
  .card {
    height: 100%;
  }

  /* Наведения на телефоне нет, а вместе с ним не было и способа добраться до
     кнопок карточки: перетаскивание пальцем тоже не работает, «Добавить в…»
     остаётся единственным путём положить блок в вариант. */
  .actions {
    opacity: 1;
  }

  /* Пять иконок в строку по ширине карточки ленты встают, а вот вместе с
     меткой «пересчитано» слева — уже нет: перенос оставляем, но ломается
     теперь metaRow, а сам ряд иконок остаётся одной строкой. */
  .metaRow {
    flex-wrap: wrap;
    row-gap: var(--sp-2);
  }

  /* Промежутки на волосок теснее общих: карточка ленты шириной ровно 232px
     (LibraryPanel.module.css), и пять кнопок по 38px с обычным зазором вылезают
     из неё на пару пикселей. Уступает зазор, а не размер кнопки: палец целится
     в кнопку, а не между ними. */
  .actions {
    flex-wrap: nowrap;
    gap: var(--sp-1);
  }

  /* Пальцу нужна цель побольше — те же 38×32, что у кнопок карточки
     таймлайна и у проверки цены. */
  .iconBtn {
    width: 38px;
    height: 32px;
  }
}
